import React, { useState } from 'react';
import { AnalysisData, OffreData } from '../../types/index';
import { StackedBar } from './StackedBar';
import { propertyService } from '@services/property.service';
import { calcDiffMarche } from '@utils/property-calcs';

interface RentalYieldAnalysisProps {
  analysis: AnalysisData;
  offreData: OffreData;
  rentPrice: number;
  isRentEstimated?: boolean;
  estimatedRent?: number;
  estimatedYield?: number;
  rentConfidence?: 'high' | 'medium' | 'low';
  mortgageRateStressTest?: number;
  roeTarget?: number;
  askingPrice?: number;
  estimatedSalePrice?: number;
  estimatedSalePriceMin?: number;
  estimatedSalePriceMax?: number;
  saleEstimateSource?: string;
  area?: number;
  propertyId?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
}

const fmt = (v: number) =>
  new Intl.NumberFormat('fr-CH', { maximumFractionDigits: 0 }).format(v);

const fmtPct = (v: number) => `${v.toFixed(1)}%`;

interface ColData {
  loyerAnnuel: number;
  chargesPPEAnnuelles: number;
  coutsMaintenance: number;
  chargesInteret: number;
  amortissement: number;
  impotFoncier: number;
  cashFlowAnnuel: number;
  cashFlowMensuel: number;
  rendementBrut: number;
  rendementNet: number;
  roe: number;
}

const YieldColumn: React.FC<{ label: string; badge: string; badgeColor: string; data: ColData }> = ({
  label,
  badge,
  badgeColor,
  data,
}) => {
  const totalAnnuel = data.loyerAnnuel;

  const segments = [
    { label: 'Cashflow', value: data.cashFlowAnnuel, color: '#3B82F6' },
    { label: 'Charges PPE', value: data.chargesPPEAnnuelles, color: '#06B6D4' },
    { label: "Charges d'intérêts", value: data.chargesInteret, color: '#F97316' },
    { label: 'Coûts de maintenance', value: data.coutsMaintenance, color: '#EAB308' },
    { label: 'Amortissement', value: data.amortissement, color: '#8B5CF6' },
    { label: 'Impôt foncier', value: data.impotFoncier, color: '#EF4444' },
  ].filter(s => s.value > 0);

  const totalSegments = segments.reduce((acc, s) => acc + s.value, 0);
  const segmentsWithPct = segments.map(s => ({
    ...s,
    percentage: totalSegments > 0 ? (s.value / totalSegments) * 100 : 0,
  }));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      {/* Badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badgeColor}`}>
          {badge}
        </span>
        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</h4>
      </div>

      {/* Indicateurs */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Rendement brut</span>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{fmtPct(data.rendementBrut)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">Rendement net</span>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{fmtPct(data.rendementNet)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">ROE</span>
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{fmtPct(data.roe)}</span>
        </div>
        <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">Cash-flow mensuel</span>
          <span className={`text-sm font-bold ${data.cashFlowMensuel >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {fmt(data.cashFlowMensuel)} CHF/mois
          </span>
        </div>
      </div>

      {/* Flux annuels */}
      <div>
        <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Flux annuels</h5>
        <StackedBar
          segments={segmentsWithPct}
          total={totalAnnuel}
          totalLabel="Loyer"
        />
      </div>
    </div>
  );
};

export const RentalYieldAnalysis: React.FC<RentalYieldAnalysisProps> = ({
  analysis,
  offreData,
  rentPrice,
  isRentEstimated,
  estimatedRent,
  estimatedYield,
  rentConfidence,
  mortgageRateStressTest,
  roeTarget,
  askingPrice,
  estimatedSalePrice,
  estimatedSalePriceMin,
  estimatedSalePriceMax,
  saleEstimateSource,
  area,
  propertyId,
  address,
  city,
  postalCode,
  latitude,
  longitude,
}) => {
  const [estimating, setEstimating] = useState(false);
  const [localEstimate] = useState<{
    price: number; min: number; max: number; source: string;
  } | null>(null);

  const handleEstimate = async () => {
    if (!propertyId) return;
    setEstimating(true);
    try {
      await propertyService.estimateSale(propertyId);
      // Recharger la page pour récupérer les nouvelles valeurs
      window.location.reload();
    } catch {
      setEstimating(false);
    }
  };

  const effectivePrice = localEstimate?.price ?? estimatedSalePrice;
  const effectiveMin = localEstimate?.min ?? estimatedSalePriceMin;
  const effectiveMax = localEstimate?.max ?? estimatedSalePriceMax;
  const effectiveSource = localEstimate?.source ?? saleEstimateSource;
  const slugify = (str: string) =>
    str.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim().replace(/\s+/g, '-');

  const { realAdvisorUrl, realAdvisorAddress } = React.useMemo(() => {
    const addr = address?.trim() ?? '';
    const cty = city?.trim() ?? '';
    const pc = postalCode?.trim() ?? '';
    const displayAddress = [addr || null, pc, cty].filter(Boolean).join(', ');

    if (!cty) return { realAdvisorUrl: 'https://realadvisor.ch/fr/valeur-immobiliere', realAdvisorAddress: displayAddress };

    const citySlug = slugify(cty);
    const prefix = pc ? `${pc}-${citySlug}` : citySlug;
    const base = `https://realadvisor.ch/fr/prix-m2-immobilier`;

    // Sans adresse → page ville
    if (!addr) {
      return { realAdvisorUrl: `${base}/${prefix}`, realAdvisorAddress: displayAddress };
    }

    // Extraire numéro (dernier token numérique)
    const parts = addr.split(/\s+/);
    const last = parts[parts.length - 1];
    const hasNumber = /^\d+[a-zA-Z]?$/.test(last);
    const streetName = hasNumber ? parts.slice(0, -1).join(' ') : addr;
    const streetNumber = hasNumber ? last : null;
    const streetSlug = slugify(streetName);
    const label = encodeURIComponent(addr);

    let url = streetNumber
      ? `${base}/${prefix}/${streetSlug}/${streetNumber}`
      : `${base}/${prefix}/${streetSlug}`;

    if (latitude && longitude) {
      url += `?lat=${latitude}&lng=${longitude}&label=${label}`;
    }

    return { realAdvisorUrl: url, realAdvisorAddress: displayAddress };
  }, [address, city, postalCode, latitude, longitude]);
  // Calcul position marché : delta entre prix demandé et prix estimé
  const marketDelta = calcDiffMarche(askingPrice, effectivePrice);
  const prixData: ColData = {
    loyerAnnuel: analysis.loyerAnnuel,
    chargesPPEAnnuelles: analysis.chargesPPEAnnuelles,
    coutsMaintenance: analysis.coutsMaintenance,
    chargesInteret: analysis.chargesInteret,
    amortissement: analysis.amortissement,
    impotFoncier: analysis.impotFoncier,
    cashFlowAnnuel: analysis.cashFlowAnnuel,
    cashFlowMensuel: analysis.cashFlowMensuel,
    rendementBrut: analysis.rendementBrut,
    rendementNet: analysis.rendementNet,
    roe: analysis.roe,
  };

  const conseilleData: ColData = {
    loyerAnnuel: analysis.loyerAnnuel,
    chargesPPEAnnuelles: analysis.chargesPPEAnnuelles,
    coutsMaintenance: offreData.coutsMaintenance,
    chargesInteret: offreData.chargesInteret,
    amortissement: offreData.amortissement,
    impotFoncier: offreData.impotFoncier,
    cashFlowAnnuel: offreData.cashFlowAnnuel,
    cashFlowMensuel: offreData.cashFlowMensuel,
    rendementBrut: offreData.rendementBrut,
    rendementNet: offreData.rendementNet,
    roe: offreData.roe,
  };

  const stressData: ColData = {
    loyerAnnuel: analysis.loyerAnnuel,
    chargesPPEAnnuelles: analysis.chargesPPEAnnuelles,
    coutsMaintenance: analysis.coutsMaintenance,
    chargesInteret: analysis.chargesInteretStressTest,
    amortissement: analysis.amortissement,
    impotFoncier: analysis.impotFoncier,
    cashFlowAnnuel: analysis.cashFlowAnnuelStressTest,
    cashFlowMensuel: analysis.cashFlowMensuelStressTest,
    rendementBrut: analysis.rendementBrut,
    rendementNet: analysis.rendementNet,
    roe: analysis.roeStressTest,
  };

  const stressBadgeColor = roeTarget != null && analysis.roeStressTest < roeTarget
    ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
    : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-400';

  const confidenceBadge = {
    high: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
    low: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };

  return (
    <div>
      <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-blue-500 text-lg">~</span>
          <h4 className="font-semibold text-blue-700 dark:text-blue-400">
            Estimation prix de marché
          </h4>
          {effectivePrice && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
              {effectiveSource === 'realadvisor' ? 'RealAdvisor' : effectiveSource === 'calibrated' ? 'Calibré (annonces)' : 'Estimateur local'}
            </span>
          )}
          <a
            href={realAdvisorUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            Vérifier ↗
          </a>
          {realAdvisorAddress && (
            <span
              className="text-xs text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Cliquer pour copier l'adresse"
              onClick={() => navigator.clipboard.writeText(realAdvisorAddress)}
            >
              📋 {realAdvisorAddress}
            </span>
          )}
          {marketDelta !== null && (
            <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${
              marketDelta < -10
                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                : marketDelta > 10
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {marketDelta < -10 ? '▼ Sous-évalué' : marketDelta > 10 ? '▲ Sur-évalué' : '● Dans la norme'}
            </span>
          )}
        </div>

        {effectivePrice ? (
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Fourchette marché</div>
              <div className="text-gray-600 dark:text-gray-300">
                {fmt(effectiveMin ?? 0)} – {fmt(effectiveMax ?? 0)} CHF
              </div>
              <div className="font-semibold text-blue-700 dark:text-blue-300 text-base mt-1">
                Médian : {fmt(effectivePrice)} CHF
              </div>
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Prix demandé</div>
              <div className="font-semibold text-gray-800 dark:text-gray-200 text-base">
                {askingPrice ? fmt(askingPrice) + ' CHF' : '—'}
              </div>
              {marketDelta !== null && (
                <div className={`text-sm font-medium mt-1 ${
                  marketDelta < -10 ? 'text-green-600 dark:text-green-400'
                  : marketDelta > 10 ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {marketDelta > 0 ? '+' : ''}{marketDelta.toFixed(1)}% vs marché
                </div>
              )}
            </div>
            <div>
              <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Prix au m²</div>
              <div className="text-gray-600 dark:text-gray-300 text-sm">
                {askingPrice && area ? `${fmt(askingPrice / area)} CHF/m²` : '—'}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
            <span>Aucune estimation disponible.</span>
            {propertyId && (
              <button
                onClick={handleEstimate}
                disabled={estimating}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50 transition-colors"
              >
                {estimating ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Estimation en cours…
                  </>
                ) : 'Lancer l\'estimation'}
              </button>
            )}
          </div>
        )}
      </div>

      {!rentPrice && estimatedRent && (
        <div className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20">
          <div className="flex items-start gap-3">
            <span className="text-amber-500 text-lg">~</span>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-semibold text-amber-700 dark:text-amber-400">
                  Estimation automatique du loyer
                </h4>
                {rentConfidence && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${confidenceBadge[rentConfidence] ?? confidenceBadge.low}`}>
                    Confiance : {rentConfidence}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Loyer estimé</div>
                  <div className="font-semibold text-amber-700 dark:text-amber-300 text-lg">
                    {estimatedRent.toLocaleString('fr-CH')} CHF/mois
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Rendement brut estimé</div>
                  <div className="font-semibold text-amber-700 dark:text-amber-300 text-lg">
                    {estimatedYield != null ? `~${estimatedYield.toFixed(1)}%` : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 dark:text-gray-400">Loyer annuel estimé</div>
                  <div className="font-semibold text-amber-700 dark:text-amber-300 text-lg">
                    {(estimatedRent * 12).toLocaleString('fr-CH')} CHF
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Estimation basée sur les données du marché local. Saisir un loyer réel dans les paramètres pour une analyse précise.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
          Rendement locatif
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {isRentEstimated ? 'Loyer estimé' : 'Loyer'} :{' '}
          <span className={`font-semibold ${isRentEstimated ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'}`}>
            {isRentEstimated && '~'}{fmt(rentPrice)} CHF/mois
          </span>
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <YieldColumn
          label="Rendement locatif"
          badge="Prix affiché"
          badgeColor="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
          data={prixData}
        />
        <YieldColumn
          label="Rendement locatif"
          badge="Offre conseillée"
          badgeColor="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400"
          data={conseilleData}
        />
        <YieldColumn
          label={`Stress test ${mortgageRateStressTest != null ? `(${mortgageRateStressTest}%)` : ''}`}
          badge="Hausse des taux"
          badgeColor={stressBadgeColor}
          data={stressData}
        />
      </div>
    </div>
  );
};
